/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import ForumSpeciesPhotoCard from "../components/ForumSpeciesPhotoCard";
import ContentReportModal from "../components/ContentReportModal";
import SearchModal from "../components/SearchModal";
import CommunityNavBadge from "../components/CommunityNavBadge";
import "../styles/BioIndexCommunityMobile.css";

import { auth, db, storage } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { deleteObject, ref } from "firebase/storage";

import {
  FORUM_CATEGORIES,
  isValidForumCategory,
} from "../utils/forumCategories";

const MAX_TOPIC_TITLE_LENGTH = 160;
const MAX_TOPIC_BODY_LENGTH = 1000;
const MAX_COMMENT_LENGTH = 5000;

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value.seconds) return value.seconds * 1000;

  return Number(value) || 0;
}

function formatRelativeTime(value) {
  const time = timestampToMillis(value);

  if (!time) return "Just now";

  const difference = Math.max(0, Date.now() - time);
  const minutes = Math.floor(difference / 60000);
  const hours = Math.floor(difference / 3600000);
  const days = Math.floor(difference / 86400000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  if (days < 30) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  return new Date(time).toLocaleDateString();
}

async function createUserNotification(recipientUid, notificationData) {
  if (!recipientUid || !notificationData?.dedupeKey) return;

  await addDoc(collection(db, "users", recipientUid, "notifications"), {
    ...notificationData,
    createdAt: serverTimestamp(),
  });
}

function getApprovalPercentage(votes) {
  let positiveVotes = 0;
  let negativeVotes = 0;

  votes.forEach((vote) => {
    if (Number(vote.value) === 1) positiveVotes++;
    if (Number(vote.value) === -1) negativeVotes++;
  });

  const totalVotes = positiveVotes + negativeVotes;

  return {
    positiveVotes,
    negativeVotes,
    totalVotes,
    percentage:
      totalVotes > 0 ? Math.round((positiveVotes / totalVotes) * 100) : null,
  };
}

function AuthorBadge({ name, photoURL, uid, onRequestProfile }) {
  const initial =
    String(name || "B")
      .trim()
      .charAt(0)
      .toUpperCase() || "B";

  return (
    <button
      type="button"
      disabled={!uid || !onRequestProfile}
      onClick={() => onRequestProfile?.({ uid, name })}
      className="flex min-w-0 items-center gap-2 rounded-full text-left transition hover:text-[#01767A] focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/25 disabled:cursor-default"
      aria-label={`Visit ${name || "BioIndex User"}'s profile`}
    >
      {photoURL ? (
        <img
          src={photoURL}
          alt=""
          className="h-9 w-9 shrink-0 rounded-full border border-[#93CA87]/40 object-cover"
        />
      ) : (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#374049] text-xs font-semibold text-white">
          {initial}
        </div>
      )}

      <div className="min-w-0">
        <div className="truncate text-xs font-semibold text-[#374049]">
          {name || "BioIndex User"}
        </div>
      </div>
    </button>
  );
}

function CommentEntry({
  topicId,
  comment,
  isReply = false,
  topicLocked,
  currentUid,
  actionCommentId,
  reportedContentKeys,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onRequestProfile,
}) {
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body || "");
  const [commentVotes, setCommentVotes] = useState([]);
  const [commentVoteSaving, setCommentVoteSaving] = useState(false);

  const deleted = !!comment.deleted;
  const hidden = !!comment.hidden;
  const isOwner = !!currentUid && currentUid === comment.authorUid;
  const saving = actionCommentId === comment.id;
  const isReported = !!reportedContentKeys[`comment_${comment.id}`];

  const voteSummary = useMemo(
    () => getApprovalPercentage(commentVotes),
    [commentVotes],
  );

  const currentUserVote = useMemo(() => {
    if (!currentUid) return 0;

    return Number(
      commentVotes.find((vote) => vote.id === currentUid)?.value || 0,
    );
  }, [commentVotes, currentUid]);

  useEffect(() => {
    if (!topicId || !comment.id) return;

    const votesRef = collection(
      db,
      "forumTopics",
      topicId,
      "comments",
      comment.id,
      "votes",
    );

    const unsubscribe = onSnapshot(
      votesRef,
      (snapshot) => {
        setCommentVotes(
          snapshot.docs.map((voteDocument) => ({
            id: voteDocument.id,
            ...voteDocument.data(),
          })),
        );
      },
      (error) => {
        console.error("Failed to load comment votes:", error);
      },
    );

    return () => unsubscribe();
  }, [topicId, comment.id]);

  async function saveEdit() {
    const saved = await onEdit(comment.id, editBody);

    if (saved) {
      setEditing(false);
    }
  }

  async function voteOnComment(value) {
    if (commentVoteSaving || !currentUid || !topicId || !comment.id) return;

    const voteRef = doc(
      db,
      "forumTopics",
      topicId,
      "comments",
      comment.id,
      "votes",
      currentUid,
    );

    setCommentVoteSaving(true);

    try {
      if (currentUserVote === value) {
        await deleteDoc(voteRef);
      } else {
        await setDoc(voteRef, {
          userUid: currentUid,
          value,
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Failed to save comment vote:", error);
    } finally {
      setCommentVoteSaving(false);
    }
  }

  return (
    <article
      className={`rounded-xl border p-3.5 sm:rounded-2xl sm:p-5 ${
        isReply
          ? "border-[#93CA87]/35 bg-[#FDFBF8]"
          : "border-[#93CA87]/40 bg-white shadow-sm"
      }`}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <AuthorBadge
            name={comment.authorName}
            photoURL={comment.authorPhotoURL}
            uid={comment.authorUid}
            onRequestProfile={onRequestProfile}
          />

          <div className="shrink-0 text-[10px] text-slate-400">
            {formatRelativeTime(comment.createdAt)}

            {comment.editedAt && (
              <span> · Edited {formatRelativeTime(comment.editedAt)}</span>
            )}
          </div>
        </div>

        {deleted ? (
          <p className="text-sm italic text-slate-400">
            {isReply ? "This reply was deleted." : "This comment was deleted."}
          </p>
        ) : hidden ? (
          <p className="text-sm italic text-slate-400">
            {isReply
              ? "This reply is unavailable."
              : "This comment is unavailable."}
          </p>
        ) : editing ? (
          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label
                htmlFor={`edit-comment-${comment.id}`}
                className="text-xs font-semibold text-slate-600"
              >
                Edit {isReply ? "Reply" : "Comment"}
              </label>

              <span className="text-[10px] text-slate-400">
                {editBody.length}/{MAX_COMMENT_LENGTH}
              </span>
            </div>

            <textarea
              id={`edit-comment-${comment.id}`}
              value={editBody}
              onChange={(event) =>
                setEditBody(event.target.value.slice(0, MAX_COMMENT_LENGTH))
              }
              rows={4}
              maxLength={MAX_COMMENT_LENGTH}
              disabled={saving}
              className="w-full resize-y rounded-xl border border-[#93CA87]/40 bg-white px-4 py-3 text-sm leading-relaxed text-[#374049] outline-none transition focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:opacity-60"
            />

            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditBody(comment.body || "");
                  setEditing(false);
                }}
                disabled={saving}
                className="rounded-xl border border-[#93CA87]/40 bg-white px-3 py-2 text-xs font-medium text-[#374049] transition hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={saveEdit}
                disabled={saving}
                className={`rounded-xl px-4 py-2 text-xs font-semibold text-white transition ${
                  saving
                    ? "cursor-not-allowed bg-slate-400"
                    : "bg-[#24C3BE] hover:bg-[#1faeaa]"
                }`}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600">
            {comment.body}
          </p>
        )}

        {!deleted && !hidden && !editing && (
          <div className="flex flex-col gap-3 border-t border-[#93CA87] pt-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {!isReply && !topicLocked && (
                <button
                  type="button"
                  onClick={() => onReply(comment)}
                  className="rounded-xl border border-[#93CA87]/40 bg-[#FDFBF8] px-3 py-1.5 text-xs font-medium text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/10"
                >
                  Reply
                </button>
              )}

              {isOwner && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setEditBody(comment.body || "");
                      setEditing(true);
                    }}
                    className="rounded-xl border border-[#93CA87]/40 bg-white px-3 py-1.5 text-xs font-medium text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/10"
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => onDelete(comment)}
                    disabled={saving}
                    className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-100 disabled:opacity-60"
                  >
                    Delete
                  </button>
                </>
              )}

              {!isOwner && currentUid && comment.authorUid && (
                <button
                  type="button"
                  onClick={() => onReport(comment, isReply)}
                  disabled={isReported}
                  className="rounded-xl border border-[#374049]/15 bg-white px-3 py-1.5 text-xs font-medium text-[#374049]/65 transition hover:border-[#374049]/30 hover:text-[#374049] disabled:cursor-default disabled:border-[#93CA87]/40 disabled:bg-[#93CA87]/10 disabled:text-[#01767A]"
                >
                  {isReported ? "Reported" : "Report"}
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-[10px] text-slate-400">
                {voteSummary.percentage == null
                  ? "No ratings yet"
                  : `${voteSummary.percentage}% helpful`}
              </span>

              <button
                type="button"
                onClick={() => voteOnComment(1)}
                disabled={commentVoteSaving}
                className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition disabled:opacity-60 ${
                  currentUserVote === 1
                    ? "border-[#24C3BE] bg-[#24C3BE] text-white"
                    : "border-[#93CA87]/40 bg-[#FDFBF8] text-[#374049] hover:bg-[#24C3BE]/10"
                }`}
              >
                Helpful
              </button>

              <button
                type="button"
                onClick={() => voteOnComment(-1)}
                disabled={commentVoteSaving}
                className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition disabled:opacity-60 ${
                  currentUserVote === -1
                    ? "border-[#374049] bg-[#374049] text-white"
                    : "border-[#93CA87]/40 bg-white text-[#374049] hover:bg-slate-50"
                }`}
              >
                Not Helpful
              </button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function CommentCard({
  topicId,
  comment,
  replies,
  topicLocked,
  currentUid,
  actionCommentId,
  reportedContentKeys,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onRequestProfile,
}) {
  return (
    <div className="space-y-3">
      <CommentEntry
        topicId={topicId}
        comment={comment}
        topicLocked={topicLocked}
        currentUid={currentUid}
        actionCommentId={actionCommentId}
        reportedContentKeys={reportedContentKeys}
        onReply={onReply}
        onEdit={onEdit}
        onDelete={onDelete}
        onReport={onReport}
        onRequestProfile={onRequestProfile}
      />

      {replies.length > 0 && (
        <div className="ml-2 space-y-3 border-l-2 border-[#93CA87]/35 pl-3 sm:ml-10 sm:pl-5">
          {replies.map((reply) => (
            <CommentEntry
              key={reply.id}
              topicId={topicId}
              comment={reply}
              isReply
              topicLocked={topicLocked}
              currentUid={currentUid}
              actionCommentId={actionCommentId}
              reportedContentKeys={reportedContentKeys}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onReport={onReport}
              onRequestProfile={onRequestProfile}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// Match the approved Community desktop navigation and five-item mobile bar.
// Keeping this local avoids changing navigation in other BioIndex pages.
function TopicNavIcon({ name }) {
  const svgProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (name === "home") {
    return (
      <svg {...svgProps}>
        <path d="M3 10.8 12 3l9 7.8V21h-6v-7H9v7H3Z" />
      </svg>
    );
  }

  if (name === "explore") {
    return (
      <svg {...svgProps}>
        <circle cx="12" cy="12" r="9" />
        <path d="m15.9 8.1-2.2 5.5-5.5 2.2 2.2-5.5Z" />
      </svg>
    );
  }

  if (name === "camera") {
    return (
      <svg {...svgProps} viewBox="0 0 32 32">
        <path d="M4.5 10.5h5.1l2.3-3h8.2l2.3 3h5.1v15h-23z" />
        <path d="M16 14.3v7.5M12.25 18.05h7.5" />
      </svg>
    );
  }

  if (name === "profile") {
    return (
      <svg {...svgProps}>
        <circle cx="12" cy="7.5" r="4" />
        <path d="M4.5 21v-2.7a7.5 7.5 0 0 1 15 0V21" />
      </svg>
    );
  }

  return (
    <svg {...svgProps}>
      <circle cx="9" cy="8" r="3" />
      <path d="M2.5 20v-2a6.5 6.5 0 0 1 13 0v2M17 5.5a3 3 0 0 1 0 5.5M17 14a5.5 5.5 0 0 1 4 5.5" />
    </svg>
  );
}

function ForumTopicNavigation({ onSearch, onVoiceSearch }) {
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreRef = useRef(null);
  useEffect(() => {
    if (!exploreOpen) return undefined;
    const outside = (event) => {
      if (!exploreRef.current?.contains(event.target)) setExploreOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") setExploreOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [exploreOpen]);
  const chooseSearch = () => { setExploreOpen(false); onSearch?.(); };
  const chooseVoice = () => { setExploreOpen(false); onVoiceSearch?.(); };
  const navLinkClass =
    "group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4";

  return (
    <>
      <div className="bioindex-v7-desktop-nav relative z-40 mx-auto hidden w-full max-w-[1360px] px-3 py-3 sm:block lg:px-0">
        <div className="flex w-full items-center gap-3 rounded-[1.6rem] border border-[#93CA87]/35 bg-[#FCFFF6] px-3 py-3 shadow-[0_16px_40px_rgba(15,36,40,0.18)] sm:px-4 lg:px-6">
          <Link
            to="/"
            aria-label="BioIndex Home"
            className="hidden shrink-0 items-center gap-3 rounded-2xl px-2.5 py-2 text-[#374049] transition hover:bg-[#93CA87]/10 lg:flex"
          >
            <img
              src="/favicon.ico"
              alt=""
              className="h-11 w-11 object-contain"
            />
            <span className="flex flex-col leading-none">
              <span
                className="text-[29px] text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                BioIndex
              </span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#01767A]/70">
                Community
              </span>
            </span>
          </Link>
          <span className="hidden h-7 w-px bg-[#374049]/10 lg:block" aria-hidden="true" />
          <nav
            aria-label="BioIndex navigation"
            className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-1"
          >
            <Link to="/" className={navLinkClass}>
              Home
            </Link>
            <Link to="/explore" className={navLinkClass}>
              Explore Taxonomy
            </Link>
            <Link to="/upload" className={navLinkClass}>
              Add Photos
            </Link>
            <Link to="/profile" className={navLinkClass}>
              Profile
            </Link>
            <Link
              to="/forum"
              aria-current="page"
              className="relative flex items-center justify-center rounded-2xl bg-[#24C3BE] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(1,118,122,0.18)]"
            >
              Community
              <CommunityNavBadge />
            </Link>
            <button type="button" onClick={onSearch} className={navLinkClass}>
              Search
            </button>
          </nav>
        </div>
      </div>

      <nav className="bioindex-m-bottom-bar" aria-label="Primary navigation">
        <div className="bioindex-m-bottom-grid">
          <Link
            to="/"
            aria-label="Home"
            className="bioindex-m-bottom-item"
          >
            <TopicNavIcon name="home" />
            <span>Home</span>
          </Link>
          <div className="bioindex-m-explore-wrap" ref={exploreRef}>
            <button type="button" className={`bioindex-m-bottom-item bioindex-m-explore-trigger${exploreOpen ? " bioindex-m-explore-active" : ""}`} onClick={() => setExploreOpen((value) => !value)} aria-label="Explore options" aria-expanded={exploreOpen} aria-controls="bioindex-topic-explore-menu">
              <TopicNavIcon name="explore" />
              <span>Explore</span>
            </button>
            {exploreOpen && (
              <div id="bioindex-topic-explore-menu" className="bioindex-m-explore-menu" role="group" aria-label="Explore options">
                <Link to="/explore" className="bioindex-m-explore-choice" onClick={() => setExploreOpen(false)}>
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><TopicNavIcon name="explore" /></span>
                  <span>Explore taxonomy</span>
                </Link>
                <button type="button" className="bioindex-m-explore-choice" onClick={chooseSearch}>
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><img src="/Search.png" alt="" /></span>
                  <span>Search BioIndex</span>
                </button>
                <button type="button" className="bioindex-m-explore-choice" onClick={chooseVoice}>
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" /></svg></span>
                  <span>Voice Search</span>
                </button>
              </div>
            )}
          </div>
          <Link
            to="/upload"
            aria-label="Add Photos"
            className="bioindex-m-bottom-item bioindex-m-add"
          >
            <span className="bioindex-m-add-circle" aria-hidden="true">
              <TopicNavIcon name="camera" />
            </span>
            <span>Add Photos</span>
          </Link>
          <Link
            to="/profile"
            aria-label="Profile"
            className="bioindex-m-bottom-item"
          >
            <TopicNavIcon name="profile" />
            <span>Profile</span>
          </Link>
          <div
            className="bioindex-m-bottom-item bioindex-m-current"
            aria-current="page"
          >
            <span className="bioindex-m-current-icon">
              <TopicNavIcon name="community" />
              <CommunityNavBadge />
            </span>
            <span>Community</span>
          </div>
        </div>
      </nav>
    </>
  );
}

export default function ForumTopicPage() {
  const { topicId } = useParams();
  const navigate = useNavigate();

  const [currentUid, setCurrentUid] = useState(
    () => auth.currentUser?.uid || "",
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [topic, setTopic] = useState(null);
  const [topicLoading, setTopicLoading] = useState(true);
  const [topicError, setTopicError] = useState("");

  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsError, setCommentsError] = useState("");

  const [votes, setVotes] = useState([]);
  const [voteSaving, setVoteSaving] = useState(false);

  const [commentBody, setCommentBody] = useState("");
  const [replyToComment, setReplyToComment] = useState(null);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [actionCommentId, setActionCommentId] = useState("");

  const [topicEditOpen, setTopicEditOpen] = useState(false);
  const [topicEditTitle, setTopicEditTitle] = useState("");
  const [topicEditCategory, setTopicEditCategory] = useState("");
  const [topicEditBody, setTopicEditBody] = useState("");
  const [topicEditSaving, setTopicEditSaving] = useState(false);
  const [topicEditError, setTopicEditError] = useState("");
  const [topicDeleting, setTopicDeleting] = useState(false);
  const [topicImageFullscreenOpen, setTopicImageFullscreenOpen] =
    useState(false);
  const [topicImageFullscreenURL, setTopicImageFullscreenURL] = useState("");

  const [reportTarget, setReportTarget] = useState(null);
  const [reportedContentKeys, setReportedContentKeys] = useState({});
  const [profileNavigationTarget, setProfileNavigationTarget] = useState(null);
  const [friendUids, setFriendUids] = useState([]);

  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [blockedByUserIds, setBlockedByUserIds] = useState([]);
  const [blockRelationshipsLoading, setBlockRelationshipsLoading] =
    useState(true);

  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        setCurrentUid(user?.uid || "");
      }),
    [],
  );

  useEffect(() => {
    if (!currentUid) {
      setFriendUids([]);
      return undefined;
    }

    return onSnapshot(
      collection(db, "users", currentUid, "friends"),
      (snapshot) => {
        setFriendUids(snapshot.docs.map((friend) => friend.id));
      },
      (error) => {
        console.error("Failed to load accepted friends:", error);
        setFriendUids([]);
      },
    );
  }, [currentUid]);

  useEffect(() => {
    if (!currentUid) {
      setBlockedUserIds([]);
      setBlockedByUserIds([]);
      setBlockRelationshipsLoading(false);
      return undefined;
    }

    let outgoingBlocksLoaded = false;
    let incomingBlocksLoaded = false;

    function finishBlockRelationshipLoading() {
      if (outgoingBlocksLoaded && incomingBlocksLoaded) {
        setBlockRelationshipsLoading(false);
      }
    }

    setBlockRelationshipsLoading(true);

    const outgoingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockerUid", "==", currentUid),
    );

    const incomingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockedUid", "==", currentUid),
    );

    const unsubscribeOutgoingBlocks = onSnapshot(
      outgoingBlocksQuery,
      (snapshot) => {
        setBlockedUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockedUid)
            .filter(Boolean),
        );

        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load blocked users:", error);
        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    const unsubscribeIncomingBlocks = onSnapshot(
      incomingBlocksQuery,
      (snapshot) => {
        setBlockedByUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockerUid)
            .filter(Boolean),
        );

        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load incoming blocks:", error);
        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    return () => {
      unsubscribeOutgoingBlocks();
      unsubscribeIncomingBlocks();
    };
  }, [currentUid]);

  useEffect(() => {
    if (!topicId) {
      setTopicError("This discussion could not be found.");
      setTopicLoading(false);
      return;
    }

    const topicRef = doc(db, "forumTopics", topicId);

    const unsubscribe = onSnapshot(
      topicRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setTopic(null);
          setTopicError("This discussion could not be found.");
          setTopicLoading(false);
          return;
        }

        const {
          collection,
          collectionName,
          collections,
          collectionId,
          collectionTitle,
          ...topicData
        } = snapshot.data();

        setTopic({
          id: snapshot.id,
          ...topicData,
        });

        setTopicError("");
        setTopicLoading(false);
      },
      (error) => {
        console.error("Failed to load forum topic:", error);
        setTopicError("This discussion could not be loaded.");
        setTopicLoading(false);
      },
    );

    return () => unsubscribe();
  }, [topicId]);

  useEffect(() => {
    if (!topicId) return;

    const votesRef = collection(db, "forumTopics", topicId, "votes");

    const unsubscribe = onSnapshot(
      votesRef,
      (snapshot) => {
        setVotes(
          snapshot.docs.map((voteDocument) => ({
            id: voteDocument.id,
            ...voteDocument.data(),
          })),
        );
      },
      (error) => {
        console.error("Failed to load topic votes:", error);
      },
    );

    return () => unsubscribe();
  }, [topicId]);

  useEffect(() => {
    if (!topicImageFullscreenOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    function handleFullscreenImageKeyDown(event) {
      if (event.key === "Escape") {
        setTopicImageFullscreenOpen(false);
        setTopicImageFullscreenURL("");
      }
    }

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", handleFullscreenImageKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", handleFullscreenImageKeyDown);
    };
  }, [topicImageFullscreenOpen]);

  useEffect(() => {
    if (!topicId) return;

    setCommentsLoading(true);
    setCommentsError("");

    const commentsQuery = query(
      collection(db, "forumTopics", topicId, "comments"),
      orderBy("createdAt", "asc"),
    );

    const unsubscribe = onSnapshot(
      commentsQuery,
      (snapshot) => {
        setComments(
          snapshot.docs.map((commentDocument) => ({
            id: commentDocument.id,
            ...commentDocument.data(),
          })),
        );

        setCommentsError("");
        setCommentsLoading(false);
      },
      (error) => {
        console.error("Failed to load topic comments:", error);
        setCommentsError("Comments could not be loaded.");
        setCommentsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [topicId]);

  const blockedRelationshipUserIdSet = useMemo(
    () => new Set([...blockedUserIds, ...blockedByUserIds]),
    [blockedUserIds, blockedByUserIds],
  );

  const topicInteractionBlocked =
    !!topic?.authorUid && blockedRelationshipUserIdSet.has(topic.authorUid);

  function requestForumProfile(person) {
    const uid = String(person?.uid || "").trim();
    const canViewProfile = uid === currentUid || friendUids.includes(uid);

    if (!uid || !canViewProfile || blockedRelationshipUserIdSet.has(uid)) {
      return;
    }

    setProfileNavigationTarget({
      uid,
      name: String(person?.name || "BioIndex User").trim(),
    });
  }

  function confirmForumProfileNavigation() {
    if (!profileNavigationTarget?.uid) return;

    const canViewProfile =
      profileNavigationTarget.uid === currentUid ||
      friendUids.includes(profileNavigationTarget.uid);

    if (!canViewProfile) {
      setProfileNavigationTarget(null);
      return;
    }

    const route =
      profileNavigationTarget.uid === currentUid
        ? "/profile"
        : `/profile/${profileNavigationTarget.uid}`;

    setProfileNavigationTarget(null);
    navigate(route);
  }

  const visibleComments = useMemo(
    () =>
      comments.filter(
        (comment) =>
          !comment.authorUid ||
          !blockedRelationshipUserIdSet.has(comment.authorUid),
      ),
    [comments, blockedRelationshipUserIdSet],
  );

  const topLevelComments = useMemo(
    () =>
      visibleComments.filter(
        (comment) =>
          !comment.parentCommentId ||
          !visibleComments.some(
            (candidate) => candidate.id === comment.parentCommentId,
          ),
      ),
    [visibleComments],
  );

  const repliesByParent = useMemo(() => {
    const groupedReplies = {};

    visibleComments.forEach((comment) => {
      if (!comment.parentCommentId) return;

      if (!groupedReplies[comment.parentCommentId]) {
        groupedReplies[comment.parentCommentId] = [];
      }

      groupedReplies[comment.parentCommentId].push(comment);
    });

    return groupedReplies;
  }, [visibleComments]);

  const voteSummary = useMemo(() => getApprovalPercentage(votes), [votes]);

  const currentUserVote = useMemo(() => {
    if (!currentUid) return 0;

    return Number(votes.find((vote) => vote.id === currentUid)?.value || 0);
  }, [currentUid, votes]);

  function openTopicEditor() {
    if (!topic) return;

    setTopicEditTitle(topic.title || "");
    setTopicEditCategory(topic.category || FORUM_CATEGORIES[0]);
    setTopicEditBody(topic.body || "");
    setTopicEditError("");
    setTopicEditOpen(true);
  }

  function closeTopicEditor() {
    if (topicEditSaving) return;

    setTopicEditOpen(false);
    setTopicEditError("");
  }

  async function saveTopicEdit() {
    if (topicEditSaving || !topicId || !topic) return;

    const title = topicEditTitle.trim();
    const body = topicEditBody.trim();
    const category = topicEditCategory.trim();

    setTopicEditError("");

    if (currentUid !== topic.authorUid) {
      setTopicEditError("You cannot edit this topic.");
      return;
    }

    if (title.length < 3) {
      setTopicEditError("The topic title must contain at least 3 characters.");
      return;
    }

    if (title.length > MAX_TOPIC_TITLE_LENGTH) {
      setTopicEditError(
        `The topic title cannot exceed ${MAX_TOPIC_TITLE_LENGTH} characters.`,
      );
      return;
    }

    if (!body) {
      setTopicEditError("The topic message cannot be empty.");
      return;
    }

    if (body.length > MAX_TOPIC_BODY_LENGTH) {
      setTopicEditError(
        `The topic message cannot exceed ${MAX_TOPIC_BODY_LENGTH.toLocaleString()} characters.`,
      );
      return;
    }

    if (!isValidForumCategory(category)) {
      setTopicEditError("Please select a valid topic category.");
      return;
    }

    setTopicEditSaving(true);

    try {
      await updateDoc(doc(db, "forumTopics", topicId), {
        title,
        category,
        body,

        searchTitle: title.toLowerCase(),
        searchBody: body.toLowerCase(),
        searchCategory: category.toLowerCase(),

        updatedAt: serverTimestamp(),
        editedAt: serverTimestamp(),
      });

      setTopicEditOpen(false);
      setTopicEditError("");
    } catch (error) {
      console.error("Failed to edit forum topic:", error);
      setTopicEditError("The topic could not be updated. Please try again.");
    } finally {
      setTopicEditSaving(false);
    }
  }

  async function deleteTopic() {
    if (
      topicDeleting ||
      !topicId ||
      !topic ||
      currentUid !== topic.authorUid
    ) {
      return;
    }

    const confirmed = window.confirm(
      "Permanently delete this topic and its entire discussion? All comments, replies, votes, notifications, and the uploaded topic image will also be deleted. This cannot be undone.",
    );

    if (!confirmed) return;

    setTopicDeleting(true);

    try {
      const commentsQuery = query(
        collection(db, "forumTopics", topicId, "comments"),
      );

      const votesQuery = query(collection(db, "forumTopics", topicId, "votes"));

      const [commentsSnapshot, votesSnapshot] = await Promise.all([
        getDocs(commentsQuery),
        getDocs(votesQuery),
      ]);

      const notificationRecipientUids = new Set();

      if (topic.authorUid) {
        notificationRecipientUids.add(topic.authorUid);
      }

      commentsSnapshot.forEach((commentDocument) => {
        const commentData = commentDocument.data();

        if (commentData.authorUid) {
          notificationRecipientUids.add(commentData.authorUid);
        }
      });

      const notificationSnapshots = await Promise.all(
        [...notificationRecipientUids].map((recipientUid) =>
          getDocs(
            query(
              collection(db, "users", recipientUid, "notifications"),
              where("topicId", "==", topicId),
            ),
          ),
        ),
      );

      const relatedDocumentDeletions = [];

      commentsSnapshot.forEach((commentDocument) => {
        relatedDocumentDeletions.push(deleteDoc(commentDocument.ref));
      });

      votesSnapshot.forEach((voteDocument) => {
        relatedDocumentDeletions.push(deleteDoc(voteDocument.ref));
      });

      notificationSnapshots.forEach((notificationSnapshot) => {
        notificationSnapshot.forEach((notificationDocument) => {
          relatedDocumentDeletions.push(deleteDoc(notificationDocument.ref));
        });
      });

      await Promise.all(relatedDocumentDeletions);

      if (topic.imageStoragePath) {
        try {
          await deleteObject(ref(storage, topic.imageStoragePath));
        } catch (storageError) {
          console.error(
            "The discussion image could not be removed:",
            storageError,
          );
        }
      }

      await deleteDoc(doc(db, "forumTopics", topicId));
    } catch (error) {
      console.error("Failed to permanently delete forum topic:", error);

      window.alert(
        "The topic and its associated discussion could not be completely deleted.",
      );
    } finally {
      setTopicDeleting(false);
    }
  }

  async function editComment(commentId, bodyValue) {
    const body = String(bodyValue || "").trim();

    if (!commentId || actionCommentId) return false;

    if (!body) {
      window.alert("The comment cannot be empty.");
      return false;
    }

    if (body.length > MAX_COMMENT_LENGTH) {
      window.alert(
        `Comments cannot exceed ${MAX_COMMENT_LENGTH.toLocaleString()} characters.`,
      );
      return false;
    }

    const comment = comments.find((candidate) => candidate.id === commentId);

    if (!comment || currentUid !== comment.authorUid) {
      return false;
    }

    setActionCommentId(commentId);

    try {
      await updateDoc(doc(db, "forumTopics", topicId, "comments", commentId), {
        body,
        updatedAt: serverTimestamp(),
        editedAt: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error("Failed to edit forum comment:", error);
      window.alert("The comment could not be updated.");
      return false;
    } finally {
      setActionCommentId("");
    }
  }

  async function softDeleteComment(comment) {
    if (
      !comment ||
      actionCommentId ||
      currentUid !== comment.authorUid
    ) {
      return;
    }

    const confirmed = window.confirm(
      comment.parentCommentId
        ? "Delete this reply? Its place in the discussion will be preserved."
        : "Delete this comment? Any replies beneath it will remain visible.",
    );

    if (!confirmed) return;

    setActionCommentId(comment.id);

    try {
      await updateDoc(doc(db, "forumTopics", topicId, "comments", comment.id), {
        deleted: true,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed to delete forum comment:", error);
      window.alert("The comment could not be deleted.");
    } finally {
      setActionCommentId("");
    }
  }

  function beginReply(comment) {
    setReplyToComment(comment);
    setCommentBody("");
    setCommentError("");

    window.setTimeout(() => {
      document
        .getElementById("forum-comment-composer")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  }

  function cancelReply() {
    if (commentSaving) return;

    setReplyToComment(null);
    setCommentBody("");
    setCommentError("");
  }

  function openTopicReport() {
    if (!topic || !topicId || !topic.authorUid) return;

    setReportTarget({
      contentType: "topic",
      topicId,
      commentId: "",
      reportedUserUid: topic.authorUid,
      reportedUserName: topic.authorName || "BioIndex User",
      contentTitle: topic.title || "",
      contentBody: topic.body || "",
      targetLabel: "this topic",
    });
  }

  function openCommentReport(comment, isReply) {
    if (!comment || !topic || !topicId || !comment.authorUid) return;

    setReportTarget({
      contentType: "comment",
      topicId,
      commentId: comment.id,
      reportedUserUid: comment.authorUid,
      reportedUserName: comment.authorName || "BioIndex User",
      contentTitle: topic.title || "",
      contentBody: comment.body || "",
      targetLabel: isReply ? "this reply" : "this comment",
    });
  }

  function markContentReported(reportKey) {
    setReportedContentKeys((currentKeys) => ({
      ...currentKeys,
      [reportKey]: true,
    }));
  }

  async function submitComment() {
    if (commentSaving) return;

    const user = auth.currentUser;
    const body = commentBody.trim();

    setCommentError("");

    if (!user) {
      setCommentError("You must be logged in to comment.");
      return;
    }

    if (!topic || topic.deleted || topic.hidden) {
      setCommentError("This discussion is no longer available.");
      return;
    }

    if (topic.locked) {
      setCommentError("This discussion is locked.");
      return;
    }

    if (!body) {
      setCommentError("Please write a comment.");
      return;
    }

    if (
      (topic.authorUid && blockedRelationshipUserIdSet.has(topic.authorUid)) ||
      (replyToComment?.authorUid &&
        blockedRelationshipUserIdSet.has(replyToComment.authorUid))
    ) {
      setCommentError("Interaction with this user is unavailable.");
      return;
    }

    if (body.length > MAX_COMMENT_LENGTH) {
      setCommentError(
        `Comments cannot exceed ${MAX_COMMENT_LENGTH.toLocaleString()} characters.`,
      );
      return;
    }

    setCommentSaving(true);

    try {
      const profileSnap = await getDoc(doc(db, "profile", user.uid));
      const profileData = profileSnap.exists() ? profileSnap.data() : {};

      const authorName =
        String(
          profileData.name ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "BioIndex User",
        ).trim() || "BioIndex User";

      const authorPhotoURL = String(profileData.photoURL || "").trim();

      const topicRef = doc(db, "forumTopics", topicId);
      const commentRef = doc(
        collection(db, "forumTopics", topicId, "comments"),
      );

      const parentCommentId = replyToComment?.id || null;

      await runTransaction(db, async (transaction) => {
        const currentTopicSnap = await transaction.get(topicRef);

        if (!currentTopicSnap.exists()) {
          throw new Error("Topic not found.");
        }

        const currentTopic = currentTopicSnap.data();

        if (
          currentTopic.deleted ||
          currentTopic.hidden ||
          currentTopic.locked
        ) {
          throw new Error("Topic unavailable.");
        }

        transaction.set(commentRef, {
          body,

          authorUid: user.uid,
          authorName,
          authorPhotoURL,

          parentCommentId,

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          editedAt: null,

          hidden: false,
          deleted: false,
        });

        transaction.update(topicRef, {
          commentCount: Number(currentTopic.commentCount || 0) + 1,
          lastActivityAt: serverTimestamp(),
        });
      });

      try {
        const notificationTargetRoute = `/forum/topic/${topicId}`;

        if (
          replyToComment?.authorUid &&
          replyToComment.authorUid !== user.uid
        ) {
          await createUserNotification(replyToComment.authorUid, {
            type: "comment_reply",

            message: `${authorName} replied to your comment`,
            detail: topic.title || "Forum discussion",
            targetRoute: notificationTargetRoute,
            read: false,

            actorUid: user.uid,
            actorName: authorName,
            actorPhotoURL: authorPhotoURL,

            topicId,
            commentId: commentRef.id,
            parentCommentId: replyToComment.id,

            titleId: "",
            genusName: "",

            dedupeKey: `comment_reply_${topicId}_${commentRef.id}`,
          });
        } else if (
          !replyToComment &&
          topic.authorUid &&
          topic.authorUid !== user.uid
        ) {
          await createUserNotification(topic.authorUid, {
            type: "topic_comment",

            message: `${authorName} commented on your topic`,
            detail: topic.title || "Forum discussion",
            targetRoute: notificationTargetRoute,
            read: false,

            actorUid: user.uid,
            actorName: authorName,
            actorPhotoURL: authorPhotoURL,

            topicId,
            commentId: commentRef.id,
            parentCommentId: "",

            titleId: "",
            genusName: "",

            dedupeKey: `topic_comment_${topicId}_${commentRef.id}`,
          });
        }
      } catch (notificationError) {
        console.error(
          "Comment published, but notification creation failed:",
          notificationError,
        );
      }

      setCommentBody("");
      setReplyToComment(null);
      setCommentError("");
    } catch (error) {
      console.error("Failed to publish forum comment:", error);

      setCommentError("Your comment could not be published. Please try again.");
    } finally {
      setCommentSaving(false);
    }
  }

  async function voteOnTopic(value) {
    if (
      voteSaving ||
      !auth.currentUser ||
      !topicId ||
      (topic?.authorUid && blockedRelationshipUserIdSet.has(topic.authorUid))
    ) {
      return;
    }

    const userUid = auth.currentUser.uid;
    const voteRef = doc(db, "forumTopics", topicId, "votes", userUid);

    setVoteSaving(true);

    try {
      if (currentUserVote === value) {
        await deleteDoc(voteRef);
      } else {
        await setDoc(voteRef, {
          userUid,
          value,
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Failed to save topic vote:", error);
    } finally {
      setVoteSaving(false);
    }
  }

  if (topicLoading || blockRelationshipsLoading) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 py-8">
        <div className="mx-auto flex min-h-96 max-w-5xl items-center justify-center rounded-3xl border border-[#93CA87]/40 bg-white">
          <div className="text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />

            <div className="mt-3 text-sm text-slate-500">
              Loading discussion...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (topicError || !topic) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 py-8">
        <div className="mx-auto max-w-2xl rounded-3xl border border-[#93CA87]/40 bg-white p-8 text-center shadow-xl">
          <h1
            className="text-3xl text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Discussion unavailable
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            {topicError || "This discussion could not be found."}
          </p>

          <Link
            to="/forum"
            className="mt-5 inline-flex rounded-xl bg-[#24C3BE] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1faeaa]"
          >
            Return to Community
          </Link>
        </div>
      </div>
    );
  }

  if (topicInteractionBlocked) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 py-8 sm:px-8">
        <div className="mx-auto max-w-2xl rounded-3xl border border-[#93CA87]/40 bg-white p-8 text-center shadow-xl">
          <h1
            className="text-4xl text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Discussion Unavailable
          </h1>

          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-500">
            You cannot view or interact with this discussion because there is a
            block between these accounts.
          </p>

          <Link
            to="/forum"
            className="mt-6 inline-flex rounded-xl bg-[#24C3BE] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1faeaa]"
          >
            Return to Community
          </Link>
        </div>
      </div>
    );
  }

  const topicUnavailable = topic.deleted || topic.hidden;

  return (
    <div className="min-h-[100dvh] bg-[#FCFFF6] pb-[calc(92px+env(safe-area-inset-bottom,0px))] sm:bg-[#374049] sm:pb-10">
      <ForumTopicNavigation onSearch={() => setSearchOpen(true)} onVoiceSearch={() => setVoiceOpen(true)} />

      {/* A compact mobile title replaces the old six-link mobile header. */}
      <div className="flex items-center justify-between gap-3 border-b border-[#93CA87]/30 bg-[#FCFFF6] px-4 py-3 sm:hidden">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/forum?view=topics"
            aria-label="Back to Forum"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#93CA87]/35 bg-white text-xl leading-none text-[#01767A]"
          >
            ‹
          </Link>
          <span
            className="truncate text-[26px] leading-none text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Discussion
          </span>
        </div>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Search BioIndex"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#374049] hover:bg-[#93CA87]/10"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" className="h-5 w-5">
            <circle cx="10.6" cy="10.6" r="6.5" />
            <path d="m16 16 5 5" />
          </svg>
        </button>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-3 pt-3 sm:px-6 sm:pt-3 lg:px-8">
        <div className="mx-auto w-full max-w-[1080px]">
          <div className="mb-3 hidden items-center justify-between gap-4 sm:flex">
            <Link
              to="/forum?view=topics"
              className="inline-flex items-center gap-2 rounded-xl px-1 py-2 text-sm font-semibold text-[#FCFFF6]/90 transition hover:text-[#24C3BE]"
            >
              <span className="text-lg leading-none" aria-hidden="true">
                ‹
              </span>
              Back to Forum
            </Link>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
              Community / Discussion
            </span>
          </div>

          <article className="overflow-hidden rounded-[1.15rem] border border-[#93CA87]/35 bg-white shadow-[0_8px_26px_rgba(55,64,73,0.07)] sm:rounded-[1.7rem] sm:shadow-[0_18px_50px_rgba(10,31,30,0.18)]">
            <div className="p-4 sm:p-7 lg:p-9">
              <div className="flex flex-col gap-4 sm:gap-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#01767A]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#01767A]">
                        {topic.category}
                      </span>

                      {topic.pinned && (
                        <span className="rounded-full border border-[#93CA87]/50 bg-[#FDFBF8] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#374049]">
                          Pinned
                        </span>
                      )}

                      {topic.locked && (
                        <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                          Locked
                        </span>
                      )}
                    </div>

                    <h1
                      className="mt-3 break-words text-[30px] leading-[1.09] text-[#374049] sm:text-[39px] lg:text-[43px]"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      {topicUnavailable
                        ? "Discussion unavailable"
                        : topic.title}
                    </h1>
                  </div>

                  <div className="flex shrink-0 flex-col items-start gap-3 sm:items-end">
                    <AuthorBadge
                      name={topic.authorName}
                      photoURL={topic.authorPhotoURL}
                      uid={topic.authorUid}
                      onRequestProfile={requestForumProfile}
                    />

                    {!topicUnavailable &&
                      currentUid &&
                      topic.authorUid && (
                        <div className="flex flex-wrap gap-2">
                          {currentUid === topic.authorUid ? (
                            <>
                              <button
                                type="button"
                                onClick={openTopicEditor}
                                className="rounded-xl border border-[#93CA87]/40 bg-[#FDFBF8] px-3 py-1.5 text-xs font-medium text-[#374049] transition hover:border-[#24C3BE]/60 hover:bg-[#24C3BE]/10"
                              >
                                Edit Topic
                              </button>

                              <button
                                type="button"
                                onClick={deleteTopic}
                                disabled={topicDeleting}
                                className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {topicDeleting ? "Deleting..." : "Delete Topic"}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={openTopicReport}
                              disabled={!!reportedContentKeys.topic}
                              className="rounded-xl border border-[#374049]/15 bg-white px-3 py-1.5 text-xs font-medium text-[#374049]/65 transition hover:border-[#374049]/30 hover:text-[#374049] disabled:cursor-default disabled:border-[#93CA87]/40 disabled:bg-[#93CA87]/10 disabled:text-[#01767A]"
                            >
                              {reportedContentKeys.topic
                                ? "Reported"
                                : "Report Topic"}
                            </button>
                          )}
                        </div>
                      )}
                  </div>
                </div>

                <div className="text-[11px] text-slate-400">
                  Posted {formatRelativeTime(topic.createdAt)}
                  {topic.editedAt && (
                    <span> · Edited {formatRelativeTime(topic.editedAt)}</span>
                  )}
                </div>

                {topicUnavailable ? (
                  <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm italic text-slate-400">
                    This discussion is no longer available.
                  </p>
                ) : topic.imageURL ? (
                  topic.imageSource === "collection" && topic.speciesPhoto ? (
                    <div className="space-y-5">
                      <ForumSpeciesPhotoCard
                        imageURL={topic.imageURL}
                        speciesPhoto={topic.speciesPhoto}
                        speciesPhotoRef={topic.speciesPhotoRef}
                        onImageClick={(resolvedImageURL) => {
                          setTopicImageFullscreenURL(
                            resolvedImageURL || topic.imageURL,
                          );
                          setTopicImageFullscreenOpen(true);
                        }}
                      />

                      <p className="whitespace-pre-wrap break-words text-sm leading-7 text-slate-600 sm:text-base">
                        {topic.body}
                      </p>
                    </div>
                  ) : (
                    <div className="grid min-w-0 grid-cols-1 overflow-hidden rounded-2xl border border-[#93CA87]/40 bg-white md:grid-cols-[minmax(240px,42%)_minmax(0,1fr)]">
                      <button
                        type="button"
                        onClick={() => {
                          setTopicImageFullscreenURL(topic.imageURL);
                          setTopicImageFullscreenOpen(true);
                        }}
                        aria-label="View topic image fullscreen"
                        className="group flex min-h-[210px] w-full cursor-zoom-in items-center justify-center overflow-hidden bg-[#FDFBF8] sm:min-h-[260px]"
                      >
                        <img
                          src={topic.imageURL}
                          alt=""
                          className="max-h-[380px] w-full object-contain transition group-hover:opacity-95"
                        />
                      </button>

                      <div className="flex min-w-0 items-center border-t border-[#93CA87]/30 bg-white px-5 py-6 md:border-l md:border-t-0 md:px-7">
                        <p className="whitespace-pre-wrap break-words text-sm leading-7 text-slate-600 [overflow-wrap:anywhere] sm:text-base">
                          {topic.body}
                        </p>
                      </div>
                    </div>
                  )
                ) : (
                  <p className="whitespace-pre-wrap break-words text-sm leading-7 text-slate-600 sm:text-base">
                    {topic.body}
                  </p>
                )}

                {!topicUnavailable && (
                  <div className="flex flex-col gap-4 border-t border-[#93CA87]/30 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                      <span>
                        {visibleComments.length}{" "}
                        {visibleComments.length === 1 ? "reply" : "replies"}
                      </span>

                      <span>
                        {voteSummary.percentage == null
                          ? "No ratings yet"
                          : `${voteSummary.percentage}% helpful`}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => voteOnTopic(1)}
                        disabled={voteSaving}
                        className={`rounded-xl border px-4 py-2 text-xs font-medium transition ${
                          currentUserVote === 1
                            ? "border-[#24C3BE] bg-[#24C3BE] text-white"
                            : "border-[#93CA87]/40 bg-[#FDFBF8] text-[#374049] hover:bg-[#24C3BE]/10"
                        }`}
                      >
                        Helpful
                      </button>

                      <button
                        type="button"
                        onClick={() => voteOnTopic(-1)}
                        disabled={voteSaving}
                        className={`rounded-xl border px-4 py-2 text-xs font-medium transition ${
                          currentUserVote === -1
                            ? "border-[#374049] bg-[#374049] text-white"
                            : "border-[#93CA87]/40 bg-white text-[#374049] hover:bg-slate-50"
                        }`}
                      >
                        Not Helpful
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </article>

          {!topicUnavailable && (
            <section className="mt-3 rounded-[1.15rem] border border-[#93CA87]/35 bg-white p-4 shadow-[0_8px_26px_rgba(55,64,73,0.06)] sm:mt-5 sm:rounded-[1.7rem] sm:p-7 sm:shadow-[0_18px_50px_rgba(10,31,30,0.16)]">
              <div className="mb-5 flex flex-wrap items-end justify-between gap-2 border-b border-[#93CA87]/30 pb-4">
                <h2
                  className="text-[29px] leading-none text-[#374049] sm:text-[34px]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  Discussion
                </h2>

                <p className="text-xs font-medium text-[#374049]/55">
                  {visibleComments.length}{" "}
                  {visibleComments.length === 1 ? "comment" : "comments"}
                </p>
              </div>

              {commentsError && (
                <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {commentsError}
                </div>
              )}

              {commentsLoading ? (
                <div className="flex min-h-40 items-center justify-center">
                  <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />
                </div>
              ) : topLevelComments.length > 0 ? (
                <div className="space-y-5">
                  {topLevelComments.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      topicId={topicId}
                      comment={comment}
                      replies={repliesByParent[comment.id] || []}
                      topicLocked={topic.locked}
                      currentUid={currentUid}
                      actionCommentId={actionCommentId}
                      reportedContentKeys={reportedContentKeys}
                      onReply={beginReply}
                      onEdit={editComment}
                      onDelete={softDeleteComment}
                      onReport={openCommentReport}
                      onRequestProfile={requestForumProfile}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-[#93CA87]/60 bg-[#FDFBF8] px-5 py-10 text-center">
                  <h3
                    className="text-2xl text-[#374049]"
                    style={{ fontFamily: "Cormorant Garamond" }}
                  >
                    No comments yet
                  </h3>

                  <p className="mt-1 text-sm text-slate-500">
                    Be the first person to join this discussion.
                  </p>
                </div>
              )}

              {!topic.locked && (
                <div
                  id="forum-comment-composer"
                  className="mt-6 rounded-2xl border border-[#93CA87]/40 bg-[#FCFFF6] p-4 sm:p-5"
                >
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3
                        className="text-2xl text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {replyToComment
                          ? `Reply to ${replyToComment.authorName || "comment"}`
                          : "Add a Comment"}
                      </h3>

                      {replyToComment && (
                        <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                          {replyToComment.body}
                        </p>
                      )}
                    </div>

                    {replyToComment && (
                      <button
                        type="button"
                        onClick={cancelReply}
                        disabled={commentSaving}
                        className="self-start rounded-xl border border-[#93CA87]/40 bg-white px-3 py-2 text-xs text-[#374049] transition hover:bg-slate-50 sm:self-auto"
                      >
                        Cancel Reply
                      </button>
                    )}
                  </div>

                  {commentError && (
                    <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {commentError}
                    </div>
                  )}

                  <div className="mb-1 flex items-center justify-between gap-3">
                    <label
                      htmlFor="forum-comment-body"
                      className="text-xs font-semibold text-slate-600"
                    >
                      Message
                    </label>

                    <span className="text-[10px] text-slate-400">
                      {commentBody.length}/{MAX_COMMENT_LENGTH}
                    </span>
                  </div>

                  <textarea
                    id="forum-comment-body"
                    value={commentBody}
                    onChange={(event) => {
                      setCommentBody(
                        event.target.value.slice(0, MAX_COMMENT_LENGTH),
                      );
                      setCommentError("");
                    }}
                    rows={5}
                    maxLength={MAX_COMMENT_LENGTH}
                    disabled={commentSaving}
                    placeholder={
                      replyToComment
                        ? "Write your reply..."
                        : "Write your comment..."
                    }
                    className="w-full resize-y rounded-xl border border-[#93CA87]/40 bg-white px-4 py-3 text-sm leading-relaxed text-[#374049] outline-none transition placeholder:text-slate-400 focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:opacity-60"
                  />

                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={submitComment}
                      disabled={commentSaving}
                      className={`rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition ${
                        commentSaving
                          ? "cursor-not-allowed bg-slate-400"
                          : "bg-[#24C3BE] hover:bg-[#1faeaa]"
                      }`}
                    >
                      {commentSaving
                        ? "Publishing..."
                        : replyToComment
                          ? "Publish Reply"
                          : "Publish Comment"}
                    </button>
                  </div>
                </div>
              )}

              {topic.locked && (
                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
                  This discussion has been locked. New comments cannot be added.
                </div>
              )}
            </section>
          )}

          <footer className="mt-5 border-t border-[#374049]/10 pb-2 pt-4 text-center sm:mt-8 sm:border-white/10">
            <p className="text-[11px] text-[#374049]/50 sm:text-white/55">
              © {new Date().getFullYear()} BioIndex™. All rights reserved.
            </p>
          </footer>
        </div>
      </div>

      {topicImageFullscreenOpen &&
        (topicImageFullscreenURL || topic.imageURL) && (
          <div
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-3 sm:p-6"
            onClick={() => {
              setTopicImageFullscreenOpen(false);
              setTopicImageFullscreenURL("");
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Fullscreen topic image"
          >
            <button
              type="button"
              aria-label="Close fullscreen image"
              onClick={(event) => {
                event.stopPropagation();
                setTopicImageFullscreenOpen(false);
                setTopicImageFullscreenURL("");
              }}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center border border-white/30 bg-black/40 text-2xl leading-none text-white transition hover:border-white/60 hover:bg-black/65 sm:right-6 sm:top-6"
            >
              ×
            </button>

            <img
              src={topicImageFullscreenURL || topic.imageURL}
              alt=""
              className="max-h-full max-w-full object-contain"
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        )}

      {topicEditOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 px-3 py-10 sm:items-center sm:px-6"
          onClick={closeTopicEditor}
        >
          <div
            className="w-full max-w-2xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FDFBF8] shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-[#93CA87]/30 bg-[#374049] px-5 py-5 text-center sm:px-7">
              <h2
                className="text-3xl text-[#FCFFF6]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Edit Topic
              </h2>
            </div>

            <div className="space-y-5 p-5 sm:p-7">
              {topicEditError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {topicEditError}
                </div>
              )}

              <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <label
                    htmlFor="edit-topic-title"
                    className="text-xs font-semibold text-slate-600"
                  >
                    Topic Title
                  </label>

                  <span className="text-[10px] text-slate-400">
                    {topicEditTitle.length}/{MAX_TOPIC_TITLE_LENGTH}
                  </span>
                </div>

                <input
                  id="edit-topic-title"
                  type="text"
                  value={topicEditTitle}
                  onChange={(event) =>
                    setTopicEditTitle(
                      event.target.value.slice(0, MAX_TOPIC_TITLE_LENGTH),
                    )
                  }
                  maxLength={MAX_TOPIC_TITLE_LENGTH}
                  disabled={topicEditSaving}
                  className="h-12 w-full rounded-xl border border-[#93CA87]/40 bg-white px-4 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:opacity-60"
                />
              </div>

              <div>
                <label
                  htmlFor="edit-topic-category"
                  className="mb-1 block text-xs font-semibold text-slate-600"
                >
                  Category
                </label>

                <select
                  id="edit-topic-category"
                  value={topicEditCategory}
                  onChange={(event) => setTopicEditCategory(event.target.value)}
                  disabled={topicEditSaving}
                  className="h-12 w-full rounded-xl border border-[#93CA87]/40 bg-white px-4 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:opacity-60"
                >
                  {FORUM_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <label
                    htmlFor="edit-topic-body"
                    className="text-xs font-semibold text-slate-600"
                  >
                    Message
                  </label>

                  <span className="text-[10px] text-slate-400">
                    {topicEditBody.length.toLocaleString()}/
                    {MAX_TOPIC_BODY_LENGTH.toLocaleString()}
                  </span>
                </div>

                <textarea
                  id="edit-topic-body"
                  value={topicEditBody}
                  onChange={(event) =>
                    setTopicEditBody(
                      event.target.value.slice(0, MAX_TOPIC_BODY_LENGTH),
                    )
                  }
                  rows={8}
                  maxLength={MAX_TOPIC_BODY_LENGTH}
                  disabled={topicEditSaving}
                  className="w-full resize-y rounded-xl border border-[#93CA87]/40 bg-white px-4 py-3 text-sm leading-relaxed text-[#374049] outline-none transition focus:border-[#24C3BE]/70 focus:ring-2 focus:ring-[#24C3BE]/15 disabled:opacity-60"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-[#93CA87]/30 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
              <button
                type="button"
                onClick={closeTopicEditor}
                disabled={topicEditSaving}
                className="rounded-xl border border-[#93CA87]/40 bg-white px-5 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={saveTopicEdit}
                disabled={topicEditSaving}
                className={`rounded-xl px-6 py-2.5 text-sm font-semibold text-white transition ${
                  topicEditSaving
                    ? "cursor-not-allowed bg-slate-400"
                    : "bg-[#24C3BE] hover:bg-[#1faeaa]"
                }`}
              >
                {topicEditSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {profileNavigationTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#374049]/75 px-4 py-8"
          onClick={() => setProfileNavigationTarget(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="forum-profile-navigation-title"
            className="w-full max-w-sm overflow-hidden rounded-3xl border border-[#93CA87]/50 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 pb-6 pt-7 text-center">
              <h2
                id="forum-profile-navigation-title"
                className="text-3xl leading-tight text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Visit profile?
              </h2>

              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Travel to {profileNavigationTarget.name || "this user"}’s
                profile page?
              </p>

              <div className="mt-6 flex gap-2.5">
                <button
                  type="button"
                  onClick={() => setProfileNavigationTarget(null)}
                  className="flex-1 rounded-xl border border-[#93CA87]/40 bg-white px-3 py-2.5 text-sm font-medium text-[#374049] transition hover:bg-slate-50"
                >
                  No
                </button>

                <button
                  type="button"
                  onClick={confirmForumProfileNavigation}
                  className="flex-1 rounded-xl bg-[#01767A] px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-[#025f62]"
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      {voiceOpen && <SearchModal open voiceOnly onClose={() => setVoiceOpen(false)} />}

      <ContentReportModal
        target={reportTarget}
        onClose={() => setReportTarget(null)}
        onSubmitted={markContentReported}
      />
    </div>
  );
}
